import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H4 } from "../../../styles/Fonts";
import AddCollateral from "../Modals/AddCollateral";
import RemoveCollateral from "../Modals/RemoveCollateral";
import { getPositionInfos } from "../../../redux/factoryAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faArrowUpRightFromSquare,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import { getLiquidationPrice } from "../../../utils/calculation/getLiquidationPrice";
import LabelWithHint from "../../../components/LabelWithHint";
import { openNewTab, getTruncNum } from "../../../utils/common";
import { Dropdown } from "react-bootstrap";
import ShareModal from "../Modals/ShareModal";
import { BeatLoader } from "react-spinners";
import { PairProps } from "../../../utils/constant/pairs";
import { useWeb3React } from "@web3-react/core";
import { UserActivePositionProps } from "../../../redux/factorySlice";
import { StateProps } from "../../../hooks/useUniswapPoolState";
import UpdateStopLoss from "../Modals/UpdateStopLoss";
import {
  closeFactoryPosition,
  collectFactoryFee,
} from "../../../utils/api/factoryV3Api";
import { CHAIN_IDS_TO_NAMES } from "../../../utils/constant/chains";
import PairTokenIcon from "../../../components/PairTokenIcon";

const ActivePosition: React.FC<{
  position: UserActivePositionProps;
  currency: string;
  pair: PairProps;
  decimal: number;
  updateUserActivePositions: () => void;
  updateUserClosedPositions: () => void;
  updateVaultBalance: () => void;
  vaultBalance: number;
  uniPoolState: StateProps;
}> = (props) => {
  const {
    position,
    pair,
    updateUserActivePositions,
    updateUserClosedPositions,
    updateVaultBalance,
    vaultBalance,
    uniPoolState,
  } = props;
  const {
    positionId,
    stopLossLowerPrice,
    stopLossUpperPrice,
    wantAmount,
    reserveAmount,
    debtValue,
    upperTickPrice,
    lowerTickPrice,
    entryPrice,
    healthFactor,
    borrowRatio,
    reserveAmountAtStart,
    positionCreateTimestamp,
    wantTokenFee,
    borrowTokenFee,
    positionValueInWantToken,
  } = position;
  const {
    wantToken,
    borrowToken,
    factoryAddress,
    lendingPoolAddress,
    deployedChainId,
  } = pair;
  const { tokenDecimal: wTokenDecimal, tokenSymbol: wTokenSymbol } = wantToken;
  const { tokenDecimal: bTokenDecimal, tokenSymbol: bTokenSymbol } =
    borrowToken;
  const { token1Price, token0Price } = uniPoolState;

  const { account, chainId, library } = useWeb3React();

  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [isShow, setIsShow] = useState<boolean>(false);
  const [liqudationPrice, setLiquidationPrice] = useState<number>(0);
  const [isSharePerfOpen, setIsSharePerfOpen] = useState<boolean>(false);
  const [addCollateralIsShow, setAddCollateralIsShow] =
    useState<boolean>(false);
  const [removeCollateralIsShow, setRemoveCollateralIsShow] =
    useState<boolean>(false);
  const [showUpdateStopLoss, setShowUpdateStopLoss] = useState<boolean>(false);

  const currentTimestamp = useMemo(
    () => Math.floor(new Date().getTime() / 1000),
    []
  );

  const handleClosePosition = async (_positionId: string) => {
    if (account && _positionId && chainId && library) {
      setIsExecuting(true);

      await closeFactoryPosition(
        account,
        factoryAddress,
        _positionId,
        uniPoolState.tick,
        (0.5 / 100) * 10000, // 0.5% slippage
        chainId,
        library
      );

      setIsExecuting(false);

      updateUserActivePositions();
      updateUserClosedPositions();
      updateVaultBalance();
      getPositionInfos(
        account,
        factoryAddress,
        lendingPoolAddress,
        wTokenDecimal,
        bTokenDecimal,
        chainId,
        library
      );
    }
  };

  const handleCollectFee = async (_positionId: string) => {
    if (account && _positionId && chainId && library) {
      setIsExecuting(true);

      await collectFactoryFee(
        account,
        factoryAddress,
        _positionId,
        uniPoolState.tick,
        (0.5 / 100) * 10000, // 0.5% slippage
        chainId,
        library
      );

      setIsExecuting(false);
      updateUserActivePositions();
      updateVaultBalance();
    }
  };

  const feeApy = useMemo(
    () =>
      token1Price && currentTimestamp && positionCreateTimestamp && wantAmount
        ? (+(+wantTokenFee + +borrowTokenFee * +token1Price) / +wantAmount) *
          ((60 * 60 * 24) / (+currentTimestamp - +positionCreateTimestamp)) *
          365 *
          100
        : 0,
    [
      currentTimestamp,
      positionCreateTimestamp,
      wantAmount,
      wantTokenFee,
      borrowTokenFee,
      token1Price,
    ]
  );

  useEffect(() => {
    const getLiquiPrice = () => {
      if (
        wantAmount &&
        borrowRatio &&
        debtValue &&
        entryPrice &&
        lowerTickPrice &&
        upperTickPrice &&
        token1Price &&
        token0Price
      ) {
        const price = getLiquidationPrice(
          +wantAmount,
          +borrowRatio,
          3,
          +debtValue,
          +entryPrice,
          +lowerTickPrice,
          +upperTickPrice,
          +(+wantTokenFee + +borrowTokenFee * +token1Price),
          +token0Price,
          +reserveAmount
        );
        setLiquidationPrice(price);
      }
    };

    getLiquiPrice();
  }, [
    wantAmount,
    borrowRatio,
    debtValue,
    entryPrice,
    lowerTickPrice,
    upperTickPrice,
    token0Price,
    token1Price,
    reserveAmount,
    wantTokenFee,
    borrowTokenFee,
  ]);

  const openUniPage = (_positionId: string) => {
    openNewTab(
      `https://app.uniswap.org/#/pool/${_positionId}?chain=${CHAIN_IDS_TO_NAMES[
        deployedChainId
      ].toLowerCase()}`
    );
  };

  return (
    <Container key={positionId}>
      <Layer1Container>
        <Layer1Col>
          <LabelContainer>
            <LabelWithHint
              labelComponent={<HeadText>Pair</HeadText>}
              hintText=""
            />
          </LabelContainer>
          <PairNameRow>
            <PairTokenIcon
              borrowTokenIcon={pair.borrowToken.tokenIcon}
              wantTokenIcon={pair.wantToken.tokenIcon}
            />
            <ValueText>{pair.label}</ValueText>
          </PairNameRow>
        </Layer1Col>
        <Layer1Col>
          <LabelContainer>
            <LabelWithHint
              labelComponent={<HeadText>Position Value</HeadText>}
              hintText="The USD value of your V3 liquidity position"
            />
          </LabelContainer>
          <ValueText>
            {`${getTruncNum(+positionValueInWantToken, 5)} ${wTokenSymbol}`}
          </ValueText>
        </Layer1Col>
        <Layer1Col>
          <LabelContainer>
            <LabelWithHint
              labelComponent={<HeadText>Fee APR</HeadText>}
              hintText="The annualized return of your fee income"
            />
          </LabelContainer>
          <ValueText>{`${getTruncNum(feeApy, 2)} %`}</ValueText>
        </Layer1Col>
        <BtnContainer>
          <ManageDropDown>
            <ManageToggle id="dropdown-basic">
              {isExecuting ? (
                <BeatLoader color={Colors.gray6} size={10} />
              ) : (
                "Manage"
              )}
            </ManageToggle>
            <ManageMenu>
              <ManageItem
                onClick={() => setAddCollateralIsShow(true)}
                disabled={isExecuting}
              >
                Add Collateral
              </ManageItem>
              <ManageItem
                onClick={() => setRemoveCollateralIsShow(true)}
                disabled={isExecuting}
              >
                Remove Collateral
              </ManageItem>
              <ManageItem
                onClick={() => handleCollectFee(positionId)}
                disabled={isExecuting}
              >
                Collect Fee
              </ManageItem>
              <ManageItem
                onClick={() => setShowUpdateStopLoss(true)}
                disabled={isExecuting}
              >
                Update Stop Loss
              </ManageItem>
              <ManageItem
                onClick={() => handleClosePosition(positionId)}
                disabled={isExecuting}
              >
                Close
              </ManageItem>
            </ManageMenu>
          </ManageDropDown>
          <ClickableIcon
            icon={faShareNodes}
            color={Colors.ivory}
            disabled={!feeApy}
            onClick={() =>
              !!feeApy ? setIsSharePerfOpen((pre) => !pre) : null
            }
          />
          <ClickableIcon
            icon={faAngleDown}
            color={Colors.ivory}
            disabled={false}
            onClick={() => setIsShow((pre) => !pre)}
          />
        </BtnContainer>
      </Layer1Container>
      <Layer2Container isShow={isShow}>
        <Layer2Row>
          <Layer2Col>
            <Row>
              <LabelWithHint
                labelComponent={<Layer2Label>Position Id</Layer2Label>}
                hintText="The NFT id of your position"
              />
              <Icon
                icon={faArrowUpRightFromSquare}
                onClick={() => {
                  openUniPage(positionId);
                }}
              />
            </Row>
            <Layer2Value>{positionId}</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Amount</Layer2Label>}
              hintText="The principal of your position"
            />
            <Layer2Value>
              {`${getTruncNum(+wantAmount, 5)} ${wTokenSymbol}`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Total Debt</Layer2Label>}
              hintText="The amount of ETH that you borrowed plus the accrued interest in this position"
            />
            <Layer2Value>
              {`${
                token0Price ? getTruncNum(+debtValue / token0Price, 5) : "-"
              } ETH (${getTruncNum(+debtValue, 5)} ${wTokenSymbol})`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Extra Collateral</Layer2Label>}
              hintText="The extra collateral (in USDC) for this position"
            />
            <Layer2Value>
              {`${getTruncNum(+reserveAmount, 5)} ${wTokenSymbol}`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>PnL</Layer2Label>}
              hintText="The profit and loss in this position"
            />
            <Layer2Value>
              {`${getTruncNum(
                +positionValueInWantToken -
                  +wantAmount -
                  +debtValue +
                  +reserveAmountAtStart,
                5
              )} ${wTokenSymbol}`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Range</Layer2Label>}
              hintText="The liquidity providing range for this position"
            />
            <Layer2Value>
              {`${getTruncNum(+lowerTickPrice, 5)} - ${getTruncNum(
                +upperTickPrice,
                5
              )} ${wTokenSymbol} `}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Stop Loss Price</Layer2Label>}
              hintText="When spot price reaches the stop loss prices, this position will be closed"
            />
            <Layer2Value>
              {`${getTruncNum(+stopLossLowerPrice, 5)} & ${getTruncNum(
                +stopLossUpperPrice,
                5
              )} ${wTokenSymbol}`}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Uniswap Fee</Layer2Label>}
              hintText="The accrued uniswap fee income for this position"
            />
            <div>
              <Layer2Value>{`${getTruncNum(
                +wantTokenFee + +borrowTokenFee * +uniPoolState?.token1Price,
                5
              )} ${wTokenSymbol} `}</Layer2Value>
              <Layer2Value>{`(${getTruncNum(
                wantTokenFee,
                5
              )} ${wTokenSymbol} & ${getTruncNum(
                borrowTokenFee,
                5
              )} ${bTokenSymbol})`}</Layer2Value>
            </div>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Position Created Time</Layer2Label>}
              hintText=""
            />
            <Layer2Value>
              {new Date(positionCreateTimestamp * 1000).toLocaleString("en")}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Entry Price</Layer2Label>}
              hintText=""
            />
            <Layer2Value>{`${getTruncNum(
              +entryPrice,
              5
            )} ${wTokenSymbol} per ${bTokenSymbol}`}</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Leverage</Layer2Label>}
              hintText="The ratio of total position value divided by you initial investment when you open the position"
            />
            <Layer2Value>{+borrowRatio + 1}X</Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Est. Liquidation Price</Layer2Label>}
              hintText="The estimated liquidation price of WETH for this position. The liquidated price is estimated based on your position value, total debt, and extra collateral"
            />
            <Layer2Value>
              {liqudationPrice
                ? `${getTruncNum(
                    +liqudationPrice,
                    5
                  )} ${wTokenSymbol} per ${bTokenSymbol}`
                : " -"}
            </Layer2Value>
          </Layer2Col>
          <Layer2Col>
            <LabelWithHint
              labelComponent={<Layer2Label>Health Factor</Layer2Label>}
              hintText="Health Factor is a risk level indicator of the position. When the Health Factor falls below 1, the position will be liquidated by liquidators. You can check our document for more information about Health Factor"
            />
            <Layer2Value>{getTruncNum(+healthFactor, 4)}</Layer2Value>
          </Layer2Col>
        </Layer2Row>
      </Layer2Container>

      {/* Modals */}
      <AddCollateral
        isShowModal={addCollateralIsShow}
        setShowModal={setAddCollateralIsShow}
        positionId={positionId}
        setIsExecuting={setIsExecuting}
        updateUserActivePositions={updateUserActivePositions}
        updateVaultBalance={updateVaultBalance}
        pair={pair}
        vaultBalance={vaultBalance}
      />
      <RemoveCollateral
        isShowModal={removeCollateralIsShow}
        setShowModal={setRemoveCollateralIsShow}
        positionId={positionId}
        setIsExecuting={setIsExecuting}
        reserveAmount={+reserveAmount}
        updateUserActivePositions={updateUserActivePositions}
        updateVaultBalance={updateVaultBalance}
        pair={pair}
      />
      <UpdateStopLoss
        isShowModal={showUpdateStopLoss}
        setShowModal={setShowUpdateStopLoss}
        positionId={positionId}
        setIsExecuting={setIsExecuting}
        updateUserActivePositions={updateUserActivePositions}
        pair={pair}
        currentPrice={+token0Price}
        previousStopLoss={{
          upperPrice: +stopLossUpperPrice,
          lowerPrice: +stopLossLowerPrice,
        }}
      />
      <ShareModal
        isShowModal={isSharePerfOpen}
        setShowModal={setIsSharePerfOpen}
        position={{
          leverage: +borrowRatio + 1,
          feeApr: feeApy,
          fee: +wantTokenFee + +borrowTokenFee * +token1Price,
          wantAmount: +wantAmount,
          startTime: +positionCreateTimestamp,
          currentTime: +currentTimestamp,
          tokenSymbol: wTokenSymbol,
          tokenDecimal: wTokenDecimal,
          pair: pair.label,
        }}
      />
    </Container>
  );
};

export default ActivePosition;

const Container = styled.div``;

const LabelContainer = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    flex: initial;
  }
`;

const HeadText = styled(H4)`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
  }
`;

const ValueText = styled(H4)`
  flex: 1 1 200px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
  }
  @media (max-width: 768px) {
    flex: initial;
  }
`;

const Layer1Container = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0;
  button {
    border-radius: 4px;
    font-size: 13px;
    line-height: 18px;
    padding: 4px 8px;
    width: auto;
    margin: auto;
    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 16px;
    }
  }
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px 0;
  }
`;

const BtnContainer = styled.div`
  flex: 1 1 160px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  @media (max-width: 768px) {
    flex: 0 0 200px;
  }
  button {
    margin: 0;
  }
  @media (max-width: 768px) {
    width: 100%;
    flex: initial;
    margin: 12px 0 6px 0;
    gap: 6px;
  }
`;

const ClickableIcon = styled(FontAwesomeIcon)<{ disabled: boolean }>`
  padding: 4px;
  cursor: ${(props) => (props.disabled ? "inherit" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Layer2Container = styled.div<{ isShow: boolean }>`
  max-height: ${(props) => (props.isShow ? "600px" : "0px")};
  transition: all ease 300ms;
  overflow-y: hidden;
`;

const Layer2Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: ${Colors.backgroundGray4};
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const Layer2Col = styled.div`
  width: 50%;
  padding: 6px 4px;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }
`;

const Layer2Label = styled(H4)`
  font-size: 12px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray3};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    flex: 1 1 100px;
  }
`;

const Layer2Value = styled(H4)`
  font-size: 13px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    text-align: right;
    flex: 1 1 100px;
  }
`;

const Layer1Col = styled.div`
  flex: 1 1 160px;
  padding: 4px 0;
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    flex: inherit;
    justify-content: space-between;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled(FontAwesomeIcon)`
  margin-left: 8px;
  color: ${Colors.ivory};
  font-size: 12px;
  cursor: pointer;
`;

const ManageDropDown = styled(Dropdown)`
  button {
    padding: 0 12px;
    width: 100px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .btn-primary {
    color: inherit;
    background-color: ${Colors.lightKhaki};
    border-color: ${Colors.lightKhaki};
    :hover {
      color: inherit;
      background-color: ${Colors.lightKhaki};
      border-color: ${Colors.lightKhaki};
    }
    :focus {
      box-shadow: none;
    }
    :active {
      transform: scale(0.95);
    }
    transition: all ease 200ms;
  }
  @media (max-width: 768px) {
    button {
      padding: 0 12px;
      width: 90px;
      min-height: 28px;
    }
  }
`;

const ManageToggle = styled(Dropdown.Toggle)`
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  min-height: 32px;
  ::after {
    margin-left: 8px;
    vertical-align: 1px;
  }
`;

const ManageMenu = styled(Dropdown.Menu)`
  background-color: ${Colors.gray6};
  box-shadow: 2px 2px 10px ${Colors.backgroundGray3};
`;

const ManageItem = styled(Dropdown.Item)`
  font-size: 13px;
  line-height: 22px;
  color: ${Colors.ivory};
  font-family: Open Sans, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  :focus {
    background-color: inherit;
  }
`;

const PairNameRow = styled.div`
  display: flex;
  align-items: center;
`;
